import _debounce from 'lodash/debounce';

type ResolvedFavoriteOption = {
  fn: {
    switch?: (elem: HTMLElement) => void;
    formAction?: (
      elem: HTMLElement,
      option: ResolvedFavoriteOption
    ) => Promise<void>;
  };
  button: {
    toggleDisabled: boolean;
    toggleMethod: false | [string, string];
  };
};

type FavoriteOption = {
  fn?: ResolvedFavoriteOption['fn'];
  button?: Partial<ResolvedFavoriteOption['button']>;
};

const DEFAULT_OPTION: ResolvedFavoriteOption = {
  fn: {},
  button: {
    toggleDisabled: true,
    toggleMethod: false,
  },
};

function getCsrfToken(): string {
  const token = document.querySelector<HTMLMetaElement>(
    'meta[name=csrf-token]'
  )?.content;
  if (!token) {
    throw new Error('CSRF token not found');
  }
  return token;
}

class Favorite {
  private readonly classname: string;
  private readonly rootElement: Document | Element;
  private readonly option: ResolvedFavoriteOption;
  private readonly switchHandler: (elem: HTMLElement) => void;
  private readonly formActionHandler: (
    elem: HTMLElement,
    option: ResolvedFavoriteOption
  ) => Promise<void>;
  private readonly buttons: NodeListOf<HTMLElement>;

  constructor(
    classname: string,
    option: FavoriteOption = {},
    rootElement: Document | Element = document
  ) {
    if (!classname) throw new Error('classname is required');
    if (!rootElement) throw new Error('rootElement is required');

    this.classname = classname;
    this.rootElement = rootElement;
    this.option = {
      fn: { ...DEFAULT_OPTION.fn, ...option.fn },
      button: { ...DEFAULT_OPTION.button, ...option.button },
    };

    this.switchHandler = this.option.fn.switch ?? this.defaultSwitch.bind(this);
    this.formActionHandler =
      this.option.fn.formAction ?? this.defaultFormAction.bind(this);

    this.buttons =
      this.rootElement.querySelectorAll<HTMLElement>(classname);
    if (this.buttons.length < 1) return;

    this.buttons.forEach((elem) => this.switchHandler(elem));
  }

  private defaultSwitch(elem: HTMLElement): void {
    const { toggleDisabled } = this.option.button;

    elem.addEventListener(
      'click',
      _debounce(() => {
        if (toggleDisabled && isToggleable(elem)) {
          elem.disabled = !elem.disabled;
        }
        void this.formActionHandler(elem, this.option).catch(console.error);
      }, 200)
    );
  }

  private async defaultFormAction(
    elem: HTMLElement,
    option: ResolvedFavoriteOption
  ): Promise<void> {
    const { dataset } = elem;
    if (!dataset.url || !dataset.method) {
      throw new Error('data-url and data-method are required');
    }
    const isElemToggleable = isToggleable(elem);
    const previousDisabled = isElemToggleable ? elem.disabled : false;
    const { toggleDisabled, toggleMethod } = option.button;

    try {
      const params = new URLSearchParams({
        favoriteable_type: dataset.key ?? '',
        favoriteable_id: dataset.value ?? '',
      });
      const response = await fetch(`${dataset.url}?${params.toString()}`, {
        method: dataset.method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`response error: ${response.status}`);
      }

      if (Array.isArray(toggleMethod)) {
        elem.dataset.method =
          dataset.method === toggleMethod[0] ? toggleMethod[1] : toggleMethod[0];
      }
    } finally {
      if (toggleDisabled && isElemToggleable) {
        elem.disabled = !previousDisabled;
      }
    }
  }

  // 複雑すぎるので外に出す
  indexManipulation(): void {
    const body = this.rootElement.querySelector('.js-favorite-post-body');
    if (!body) return;

    const content = document.createElement('div');
    const h4 = document.createElement('h4');

    content.classList.add('mypage-Article_Content');
    h4.classList.add('mypage-Article_Label');
    h4.textContent = '投稿がありません';

    content.appendChild(h4);
    body.innerHTML = '';
    body.appendChild(content);
  }
}

class FavoriteMyPage {
  constructor(classname: string) {
    if (classname === undefined) {
      throw new Error('classname is required');
    }

    new Favorite(classname, {
      fn: {
        formAction: this.removeFavoriteAction.bind(this),
      },
    });
  }

  async removeFavoriteAction(
    elem: HTMLElement,
    option: ResolvedFavoriteOption
  ): Promise<void> {
    const { dataset } = elem;
    if (!dataset.url || !dataset.method || !dataset.key || !dataset.value) {
      throw new Error(
        'data-url, data-method, data-key, and data-value are required'
      );
    }
    const isElemToggleable = isToggleable(elem);
    const previousDisabled = isElemToggleable ? elem.disabled : false;
    const { toggleDisabled } = option.button;

    try {
      const response = await fetch(
        `${dataset.url}?${dataset.key}_id=${dataset.value}`,
        {
          method: dataset.method,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCsrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`response error: ${response.status}`);
      }

      if (dataset.targetModal === undefined) {
        throw new Error('data-target-modal is required');
      }

      const modal = document.querySelector<HTMLElement>(
        `[data-modal-name="${dataset.targetModal}"]`
      );
      if (modal === null) {
        throw new Error('modal is not found');
      }

      modal.removeAttribute('open');

      this.removeFavoriteDOM(elem);
    } finally {
      if (toggleDisabled && isElemToggleable) {
        elem.disabled = !previousDisabled;
      }
    }
  }

  removeFavoriteDOM(relative: HTMLElement): void {
    const { dataset } = relative;
    if (dataset.key === undefined || dataset.value === undefined) {
      throw new Error('data-key or data-value is required');
    }

    const target = document.querySelector<HTMLElement>(
      [
        dataset.removeClass ?? '',
        `[data-value="${dataset.value}"]`,
        `[data-key="${dataset.key}"]`,
      ].join('')
    );

    dataset.value = '';
    dataset.key = '';

    if (target === null) {
      throw new Error('target is not found');
    }

    const articleSelector = target.dataset.article;
    if (!articleSelector) {
      throw new Error('target.dataset.article is required');
    }
    const articles = document.querySelectorAll(articleSelector);
    if (articles.length < 1) {
      throw new Error('articles is not found');
    }

    setTimeout(() => {
      articles.forEach((article) => article.remove());
      const panels = document.querySelectorAll<HTMLElement>(
        '[data-favorite-panel]'
      );
      if (panels.length < 1) {
        throw new Error('panels is not found');
      }

      panels.forEach((panel) => {
        if (panel.children.length >= 1) return;

        const article = document.createElement('article');
        const content = document.createElement('div');
        const h4 = document.createElement('h4');

        article.classList.add('mypage-Article');
        content.classList.add('mypage-Article_Content');
        content.classList.add('-favorite');
        h4.classList.add('mypage-Article_Isnt');
        h4.textContent = 'お気に入りがありません';
        content.appendChild(h4);
        article.appendChild(content);

        panel.nextElementSibling?.remove();
        panel.classList.remove(...panel.classList);
        panel.classList.add('mypage-Panel_Body');
        panel.appendChild(article);
      });
    }, 400);
  }
}

function isToggleable(
  elem: HTMLElement
): elem is HTMLButtonElement | HTMLInputElement {
  return elem instanceof HTMLButtonElement || elem instanceof HTMLInputElement;
}

export { Favorite, FavoriteMyPage };
